<template>
  <Dialog
    :visible="modelValue"
    modal
    header="Reject Transcript Request"
    class="w-full max-w-md"
    :draggable="false"
    @update:visible="emit('update:modelValue', $event)"
  >
    <div class="space-y-3 pt-2">
      <p class="text-sm text-gray-600">
        Provide a reason for rejecting this request. The applicant will be notified by email.
      </p>
      <Textarea
        v-model="reason"
        rows="4"
        autoResize
        class="w-full"
        placeholder="Reason for rejection (min 5 characters)..."
        data-testid="reject-reason-input"
        :class="{ 'p-invalid': getErrorMessage('reason') }"
      />
      <small v-if="getErrorMessage('reason')" class="text-red-500">{{ getErrorMessage('reason') }}</small>
    </div>

    <template #footer>
      <Button size="small"
        label="Cancel"
        severity="secondary"
        text
        data-testid="reject-cancel"
        @click="close"
      />
      <Button size="small"
        label="Reject Request"
        severity="danger"
        :loading="rejecting"
        :disabled="rejecting || reason.length < 5"
        data-testid="reject-confirm"
        @click="submit"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { useFormErrors } from '@/composables/useFormErrors';
import { useTranscriptMutations } from '@/composables/transcript/useTranscriptMutations';

const props = defineProps<{
  modelValue: boolean;
  requestId: string | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  rejected: [];
}>();

const reason = ref('');
const { handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();
const { rejectRequest, rejecting } = useTranscriptMutations();

watch(() => props.modelValue, (open) => {
  if (open) {
    reason.value = '';
    clearErrors();
  }
});

const close = () => emit('update:modelValue', false);

const submit = async() => {
  if (!props.requestId) return;
  clearErrors();
  try {
    await rejectRequest(props.requestId, reason.value);
    emit('rejected');
    close();
  } catch (err) {
    handleServerErrors(err);
  }
};
</script>
